import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Id, toast } from 'react-toastify'

import { patientProfilesApi } from '../api/services/dd_panel_patient_profiles'
import {
  CreatePatientProfilePayload,
  FetchResponse
} from '../api/services/dd_panel_patient_profiles/types.ts'
import { PatientProfile } from '../api/services/orders/types.ts'

type MutationToastContext = {
  toastId: Id
}

type UsePatientProfileMutationsParams = {
  onSuccess?: () => void
}

type EditPatientProfileVariables = {
  profilePatientId: number
  profilePatientData: CreatePatientProfilePayload
}

export const usePatientProfileMutations = ({
  onSuccess
}: UsePatientProfileMutationsParams) => {
  const queryClient = useQueryClient()

  const showLoadingToast = (message: string): MutationToastContext => ({
    toastId: toast.loading(message)
  })

  const updateToast = (
    toastId: Id,
    message: string | null | undefined,
    type: 'success' | 'error'
  ) => {
    toast.update(toastId, {
      render: message,
      type,
      delay: 500,
      isLoading: false,
      autoClose: 2000
    })
  }

  const handleMutationSuccess = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['PatientProfiles'],
      refetchType: 'active'
    })

    onSuccess?.()
  }

  const handleMutationError = (error: Error) => {
    console.error('Error updating body measurement:', error)
  }

  const createPatientProfileMutation = useMutation<
    FetchResponse<PatientProfile>,
    Error,
    CreatePatientProfilePayload,
    MutationToastContext
  >({
    mutationFn: (profilePatientData) =>
      patientProfilesApi.createProfile<
        PatientProfile,
        CreatePatientProfilePayload
      >(profilePatientData),

    onMutate: () => showLoadingToast('Wysyłanie danych ...'),

    onSuccess: async (response, _variables, context) => {
      const isSuccess = response.status === 201

      updateToast(
        context.toastId,
        isSuccess ? 'Profil pacjenta został stworzony' : response.error,
        isSuccess ? 'success' : 'error'
      )

      if (isSuccess && response.data) {
        await handleMutationSuccess()
      }
    },

    onError: (error, _variables, context) => {
      if (context) {
        updateToast(context.toastId, 'Nie udało się wysłać danych', 'error')
      }

      handleMutationError(error)
    }
  })

  const editPatientProfileMutation = useMutation<
    FetchResponse<PatientProfile>,
    Error,
    EditPatientProfileVariables,
    MutationToastContext
  >({
    mutationFn: ({
      profilePatientId,
      profilePatientData
    }: EditPatientProfileVariables) =>
      patientProfilesApi.editProfile<
        PatientProfile,
        CreatePatientProfilePayload
      >(profilePatientId, profilePatientData),

    onMutate: () => showLoadingToast('Wysyłanie danych ...'),

    onSuccess: async (response, _variables, context) => {
      const isSuccess = response.status === 200

      updateToast(
        context.toastId,
        isSuccess ? 'Zmiany zostały zapisane' : response.error,
        isSuccess ? 'success' : 'error'
      )

      if (isSuccess && response.data) {
        await handleMutationSuccess()
      }
    },

    onError: (error, _variables, context) => {
      if (context) {
        updateToast(context.toastId, 'Nie udało się wysłać danych', 'error')
      }

      handleMutationError(error)
    }
  })


    const deletePatientProfileMutation = useMutation<
      FetchResponse<PatientProfile>,
      Error,
      number,
      MutationToastContext
    >({
      mutationFn: (profilePatientId: number) =>
        patientProfilesApi.deleteProfile(profilePatientId),

      onMutate: () => showLoadingToast('Usuwanie danych ...'),

      onSuccess: async (response, _variables, context) => {
        const isSuccess = response.status === 204

        updateToast(
          context.toastId,
          isSuccess ? 'Dane pacjenta zostały usunięte' : response.error,
          isSuccess ? 'success' : 'error'
        )

        if (isSuccess) {
          await handleMutationSuccess()
        }
      },

      onError: (error, _variables, context) => {
        if (context) {
          updateToast(context.toastId, 'Nie udało się usunąć danych', 'error')
        }

        handleMutationError(error)
      }
    })

  return {
    createPatientProfileMutation,
    editPatientProfileMutation,
    deletePatientProfileMutation
  }
}
